import { Stage, Layer, Line, Group, Rect, Text, Circle, Path } from "react-konva";
import { useDesign, payloadWithoutId } from "../../lib/store";
import { useCoarsePointer, useElementSize } from "../../lib/useViewportSize";
import { useFitToRoom } from "../../lib/useFitToRoom";
import { useCssInsets, type CssInsets } from "../../lib/useCssInsets";
import { useStagePanZoom } from "../../lib/useStagePanZoom";
import { dragsImmediately, SLOP_PX, takeAdjustHint, TOUCH_DRAG_DISTANCE } from "../../lib/touchPickup";
import { pressedTouch, startNodePickup, useTouchPickup } from "../../lib/useTouchPickup";
import { HoldHint } from "./HoldHint";
import { annotationScale } from "../../lib/canvasLabels";
import {
  boundingBox,
  clampInside,
  distance,
  formatArea,
  formatLength,
  FURNITURE_SNAP_INCHES,
  localToWorld,
  lockToDominantAxis,
  midpoint,
  pointInPolygon,
  polygonArea,
  snapPolygonToFurniture,
  snapPolygonToGrid,
  wallAngleDeg,
} from "../../lib/geom";
import { resolveFurniture } from "../../lib/furniture/resolve";
import { rotate } from "../../lib/furniture/geometry";
import { useInspectorInset } from "../../lib/inspectorInset";
import { sinkTrackFor } from "../../lib/furniture/templates/countertops";
import { pointHitsSink, roomToPieceLocal } from "../../lib/furniture/sinkPath";
import { mountOnWall, openingOnWall, slidingPanels } from "../../lib/floorPlanMarkers";
import { clampPoseInRoom, interiorSegments } from "../../lib/interiorWalls";
import type { Furniture, Vec2 } from "../../lib/types";
import { FurnitureNode } from "./FurnitureNode";
import { FurnitureHandles, type KnobField } from "./FurnitureHandles";
import { SinkSlideHint } from "./SinkSlideHint";
import { ModuleEditorLayer } from "./ModuleEditorLayer";
import { ModuleTray } from "./ModuleTray";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import type Konva from "konva";
import { Locate } from "lucide-react";
import { WallActionChip, wallTapIntent } from "./WallActionChip";
import { setCursor, setDragging } from "../../lib/presence";
import { track } from "../../lib/telemetry";
import { furnitureMovedName } from "../../lib/furnitureMove";
import { useAuth } from "../../lib/auth";
import { CollaboratorCursors } from "./CollaboratorCursors";
import { CollaboratorSelections } from "./CollaboratorSelections";
import { CollaboratorDragPreviewsDesign } from "./CollaboratorDragPreviews";
import { useCollaborators } from "../../lib/useCollaborators";
import { MeasureOverlay } from "./MeasureOverlay";
import { FengShuiDemoLayer } from "./FengShuiDemoLayer";
import { useFengShuiDemo } from "../../lib/fengShuiDemo";
import { gridStepsFor } from "../../lib/gridStep";
import { GridLegend } from "./GridLegend";
import { planFloorFill } from "../../lib/finishes/plan2d";
import { ROOM_FINISHES_FLAG, TOUCH_ADJUST_MODE_FLAG, useFlag } from "../../lib/flags";
import { adjustRingLayout, ADJUST_RING } from "../../lib/furniture/transform";
import { noteAdjustChange } from "../../lib/transformTelemetry";
import { isRender3dOpen } from "../../lib/render3dRequests";

interface Props {
  /** If true, allows interacting with furniture. */
  interactive: boolean;
  /** If true, shows wall labels (lengths). */
  showWallLabels?: boolean;
  /** If true, clicking on a wall enters wall-elevation mode. */
  enableWallSelect?: boolean;
  /** Touch taps on a wall only highlight it and offer Move wall / Add doors
   *  & art, so a stray tap doesn't leave this view. Mouse clicks still open
   *  the elevation directly. */
  confirmWallOpen?: boolean;
  /** Hide person silhouette. */
  hidePerson?: boolean;
  /** Hide the floating area / ceiling pill. */
  hideInfoBadge?: boolean;
  /**
   * Optional handler for right-click on a furniture piece. Receives the
   * piece's id along with the viewport coordinates of the click so the parent
   * can pop a context menu at the cursor. The default browser context menu
   * is always suppressed when this is set.
   */
  onFurnitureContextMenu?: (id: string, position: { x: number; y: number }) => void;
  /** Chrome floating over the canvas edges (CSS lengths); the room fits
   *  inside what's left. */
  fitInsets?: CssInsets;
  /** A knob was tapped in Adjust mode: open that field in the details sheet. */
  onKnobTap?: (id: string, field: KnobField) => void;
  /** The selection bar offers Adjust: coach it once on the first selection. */
  adjustHint?: boolean;
}

/** Visible spacing of the room-internal accent grid's minor lines. The
 *  furniture-snap step is user-configurable via `gridSnapInches` and can be
 *  finer than this — this constant only controls visuals. */
const GRID_INCHES = 6;
/** Major (heavier) grid line every foot. */
const MAJOR_GRID_INCHES = 12;
const WALL_THICKNESS_PX = 7;
/** Room kept around a revealed piece for its handles (rotate grip + stem), screen px. */
const ROTATE_GRIP_ALLOWANCE_PX = 52;

/** Hard cap on grid lines per axis. The visible viewport (plus its
 *  one-screen drag buffer) at the smallest configured zoom band tops out
 *  around ~2,000 lines on a 4K display, so this is ~5x normal headroom.
 *  The cap is a defense-in-depth guard: if a future bug ever feeds
 *  Infinity-but-finite bounds (e.g. a 10,000ft room from an import
 *  parser), we render a clamped grid rather than freeze the tab. */
const MAX_GRID_LINES = 10000;

// Visual palette for the two grids. The "main" grid is a subtle gray reference
// that lives in world coordinates; the "room internal" grid is a visibly
// distinct accent that lives in room coordinates and is what furniture snaps to.
//
// The main grid uses three tiers so it can crossfade subdivisions in/out as
// the user zooms: COARSE is the dominant subdivision at the current zoom and
// renders at full opacity; FINE is the next-finer subdivision and fades in as
// zoom approaches the next breakpoint; MAJOR is the always-on every-foot
// reference. Sorted darkest → lightest so the "foot" lines stay readable
// against the coarse + fine subdivisions on top of them.
const MAIN_GRID_MAJOR = "#bcc0c8";
const MAIN_GRID_COARSE = "#dadde2";
const MAIN_GRID_FINE = "#eef0f3";
const ROOM_GRID_MINOR = "rgba(42, 112, 245, 0.14)";
const ROOM_GRID_MAJOR = "rgba(42, 112, 245, 0.28)";

/** Latest furniture-node handlers. The nodes themselves keep one stable
 *  callback each so `memo` can skip pieces that didn't change. */
type FurnitureNodeHandlers = {
  onTouchStart: (e: KonvaEventObject<TouchEvent>) => void;
  onMouseMove: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave: (e: KonvaEventObject<MouseEvent>) => void;
  onDblClick: (e: KonvaEventObject<MouseEvent>) => void;
  onDblTap: (e: KonvaEventObject<Event>) => void;
  onMouseDown: (e: KonvaEventObject<MouseEvent>) => void;
  onContextMenu: (e: KonvaEventObject<PointerEvent>) => void;
  onTap: (e: KonvaEventObject<Event>) => void;
  onDragStart: (e: KonvaEventObject<DragEvent>) => void;
  onDragMove: (e: KonvaEventObject<DragEvent>) => void;
  onDragEnd: (e: KonvaEventObject<DragEvent>) => void;
};

function furnitureIdFromEvent(e: KonvaEventObject<Event>): string {
  const from = (start: Konva.Node | null): string => {
    let node = start;
    while (node) {
      const id = node.id();
      if (id) return id;
      node = node.getParent();
    }
    return "";
  };
  return from(e.currentTarget) || from(e.target);
}

export function RoomCanvas({
  interactive,
  showWallLabels = true,
  enableWallSelect = false,
  confirmWallOpen = false,
  hidePerson = false,
  hideInfoBadge = false,
  onFurnitureContextMenu,
  fitInsets,
  onKnobTap,
  adjustHint = false,
}: Props) {
  const [containerRef, size] = useElementSize<HTMLDivElement>();
  const room = useDesign((s) => s.design.room);
  const finishesOn = useFlag(ROOM_FINISHES_FLAG);
  const floorFill = (finishesOn && planFloorFill(room.finishes)) || "#ffffff";
  const furniture = useDesign((s) => s.design.furniture);
  const mounts = useDesign((s) => s.design.mounts);
  const openings = useDesign((s) => s.design.openings);
  const person = useDesign((s) => s.design.person);
  const unit = useDesign((s) => s.design.unit);
  const selectedId = useDesign((s) => s.selectedFurnitureId);
  const flashedIds = useDesign((s) => s.flashedFurnitureIds);
  const selectFurniture = useDesign((s) => s.selectFurniture);
  const updateFurniture = useDesign((s) => s.updateFurniture);
  const addFurniture = useDesign((s) => s.addFurniture);
  const beginGesture = useDesign((s) => s.beginGesture);
  const endGesture = useDesign((s) => s.endGesture);
  const cancelGesture = useDesign((s) => s.cancelGesture);
  const snapEnabled = useDesign((s) => s.snapEnabled);
  const gridSnapInches = useDesign((s) => s.gridSnapInches);
  const furnitureSnapEnabled = useDesign((s) => s.furnitureSnapEnabled);
  const designTool = useDesign((s) => s.designTool);
  const selectedFurniture = useMemo(
    () => (selectedId ? furniture.find((f) => f.id === selectedId) ?? null : null),
    [furniture, selectedId]
  );
  const editingModulesId = useDesign((s) => s.editingModulesId);
  const adjustingId = useDesign((s) => s.adjustingFurnitureId);
  const exitAdjustMode = useDesign((s) => s.exitAdjustMode);
  const adjustModeOn = useFlag(TOUCH_ADJUST_MODE_FLAG);
  const setEditingModules = useDesign((s) => s.setEditingModules);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  // Modules of the piece currently in "Edit modules" mode (null otherwise).
  const editingModules =
    selectedFurniture &&
    editingModulesId === selectedFurniture.id &&
    selectedFurniture.model?.kind === "modular"
      ? selectedFurniture.model.modules
      : null;
  const setActiveWallIndex = useDesign((s) => s.setActiveWallIndex);
  const activeWallIndex = useDesign((s) => s.activeWallIndex);
  const activeInteriorWall = useDesign((s) => s.activeInteriorWall);
  const setView = useDesign((s) => s.setView);
  const setPerson = useDesign((s) => s.setPerson);
  const sharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const setDraggingFurniture = useDesign((s) => s.setDraggingFurniture);
  const auth = useAuth();
  // Collaborator state for live cursors + selection halos. The hook returns
  // an empty Map when no shared room is active or the user is signed-out,
  // so private-room rendering is a free-of-charge no-op.
  const collaborators = useCollaborators(sharedRoomId, auth.user?.uid ?? null);

  const chrome = useCssInsets(fitInsets);
  // While the feng shui landing's callouts are on the canvas, wall lengths
  // and the person marker step aside so the callouts read cleanly.
  const demoActive = useFengShuiDemo().phase !== "idle";
  const fit = useFitToRoom(room.points, size.width, size.height, 56, undefined, chrome.insets);
  const inspector = useInspectorInset();
  const panZoom = useStagePanZoom({ onInterrupt: () => handlePinchInterrupt() });
  const measureActive = interactive && designTool === "measure";
  const personDragRef = useRef(false);
  const coarse = useCoarsePointer();
  const pickup = useTouchPickup();
  const [showAdjustHint, setShowAdjustHint] = useState(false);
  useEffect(() => {
    if (!interactive || !adjustHint || !selectedId || adjustingId) return;
    if (!takeAdjustHint()) return;
    // Lands just after the selection bar slides in.
    const show = setTimeout(() => setShowAdjustHint(true), 250);
    const hide = setTimeout(() => setShowAdjustHint(false), 3450);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
      setShowAdjustHint(false);
    };
  }, [interactive, adjustHint, selectedId, adjustingId]);
  /** Stashed on mousedown so Option/Alt+click+drag can clone even though
   *  Konva's dragstart fires after the pointer has already moved. */
  const cloneOnDragRef = useRef(false);
  const shiftHeldRef = useRef(false);
  const furnitureDragRef = useRef<{
    id: string;
    origin: Vec2;
    lastPointerRoom: Vec2;
    /** Snapped room position, committed to the store when the drag ends. */
    pose: Vec2 | null;
    node: Konva.Node;
  } | null>(null);
  /** Second click of a double-click (or a double-tap arm) is dragging the sink, not the counter. */
  const sinkDragRef = useRef<{ id: string } | null>(null);
  /** Set when a sink drag ends, so the dbltap that follows a mouse double-click doesn't arm another drag. */
  const sinkDragEndedAt = useRef(0);
  /** Swallow the furniture dragend Konva fires after we stopDrag a sink gesture. */
  const ignoreFurnitureDragEnd = useRef<string | null>(null);
  const sinkArmRef = useRef<string | null>(null);
  const stopSinkListeners = useRef<(() => void) | null>(null);
  const [sinkDraggingId, setSinkDraggingId] = useState<string | null>(null);
  /** Selected counter whose sink is under the pointer. Firms up the slide hint. */
  const [sinkHoverId, setSinkHoverId] = useState<string | null>(null);
  const furnitureHandlersRef = useRef<FurnitureNodeHandlers | null>(null);
  /** Pixel centers keyed by piece id, reused while the piece doesn't move
   *  so FurnitureNode's memo sees the same object. */
  const centerCacheRef = useRef(new Map<string, { x: number; y: number }>());
  const nodeEvents = useMemo<FurnitureNodeHandlers>(
    () => ({
      onTouchStart: (e) => furnitureHandlersRef.current?.onTouchStart(e),
      onMouseMove: (e) => furnitureHandlersRef.current?.onMouseMove(e),
      onMouseLeave: (e) => furnitureHandlersRef.current?.onMouseLeave(e),
      onDblClick: (e) => furnitureHandlersRef.current?.onDblClick(e),
      onDblTap: (e) => furnitureHandlersRef.current?.onDblTap(e),
      onMouseDown: (e) => furnitureHandlersRef.current?.onMouseDown(e),
      onContextMenu: (e) => furnitureHandlersRef.current?.onContextMenu(e),
      onTap: (e) => furnitureHandlersRef.current?.onTap(e),
      onDragStart: (e) => furnitureHandlersRef.current?.onDragStart(e),
      onDragMove: (e) => furnitureHandlersRef.current?.onDragMove(e),
      onDragEnd: (e) => furnitureHandlersRef.current?.onDragEnd(e),
    }),
    []
  );

  useEffect(() => {
    return () => stopSinkListeners.current?.();
  }, []);

  // Shift axis-lock: kick in/out even if the pointer is still. Re-apply the
  // current drag with the new shift state so the piece snaps to the locked
  // axis without a wiggle.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Shift") return;
      if (shiftHeldRef.current) return;
      shiftHeldRef.current = true;
      const ds = furnitureDragRef.current;
      if (ds) applyFurnitureDrag(ds.id, ds.lastPointerRoom, ds.node);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key !== "Shift") return;
      if (!shiftHeldRef.current) return;
      shiftHeldRef.current = false;
      const ds = furnitureDragRef.current;
      if (ds) applyFurnitureDrag(ds.id, ds.lastPointerRoom, ds.node);
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // applyFurnitureDrag closes over fresh store/fit via the render; the
    // listeners only need the refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!adjustingId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape" || isRender3dOpen()) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      exitAdjustMode("escape");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [adjustingId, exitAdjustMode]);

  /** Other pieces' outlines (room inches), for keeping touch grips off them.
   *  A piece the selection stands on (a rug) doesn't count. */
  const handleNeighbors = useMemo(() => {
    if (!coarse || !selectedFurniture) return [];
    const at = { x: selectedFurniture.x, y: selectedFurniture.y };
    return furniture
      .filter((f) => f.id !== selectedFurniture.id)
      .map((f) => localToWorld(resolveFurniture(f).footprint, f.x, f.y, f.rotation))
      .filter((outline) => !pointInPolygon(at, outline));
  }, [coarse, furniture, selectedFurniture]);

  const points = room.points;
  const bb = useMemo(() => boundingBox(points), [points]);
  const area = useMemo(() => polygonArea(points), [points]);
  /** Bbox centre — "inside" for deciding which side of a wall is outside. */
  const roomCentroid = useMemo(
    () => ({ x: bb.minX + bb.width / 2, y: bb.minY + bb.height / 2 }),
    [bb]
  );

  // Person screen size: shoulder breadth derived from weight (~ inches).
  const shoulderIn = useMemo(() => {
    // Rough approximation: shoulder breadth ~ 16-20" for adults; scale slightly with weight.
    const base = 17;
    return base + (person.weightLbs - 170) * 0.025;
  }, [person.weightLbs]);

  // Person default position = centroid of room
  const personPos: Vec2 = useMemo(() => {
    if (person.position) return person.position;
    return { x: (bb.minX + bb.maxX) / 2, y: (bb.minY + bb.maxY) / 2 };
  }, [person.position, bb]);

  // Flat polygon points for Konva
  const flatPolygon = useMemo(() => {
    const arr: number[] = [];
    for (const p of points) {
      const pp = fit.pointToPx(p);
      arr.push(pp.x, pp.y);
    }
    return arr;
  }, [points, fit]);

  /** Origin the *room's internal grid* is anchored to. We use the top-left of
   *  the room's bounding box so the first grid line sits exactly on the room's
   *  edge and every cell of the grid belongs to the room, not the world.
   *  Furniture snaps to this origin (see {@link handleFurnitureDragMove}). */
  const gridOrigin: Vec2 = useMemo(
    () => ({ x: bb.minX, y: bb.minY }),
    [bb.minX, bb.minY]
  );

  type GridLine = { points: number[]; key: string };
  type GridSet = { minor: GridLine[]; major: GridLine[] };
  type AdaptiveGridSet = {
    major: GridLine[];
    coarse: GridLine[];
    fine: GridLine[];
  };

  /** Build a grid spanning [minX, maxX] × [minY, maxY] room-coords, anchored
   *  to {@link originX}/{@link originY}. The grid uses `GRID_INCHES` spacing
   *  for minor lines and `MAJOR_GRID_INCHES` for the heavier "every foot" lines.
   *  Used for the room-internal accent grid (which has a fixed step).
   *
   *  Hardened against non-finite inputs: any NaN/Infinity in the bounds
   *  short-circuits to an empty grid. Without this, a NaN in `room.points`
   *  (which feeds `bb`/`fit.scale`) propagates into `Math.ceil(NaN)` →
   *  `kx = NaN` → `NaN > maxX` is `false` forever → infinite loop that
   *  freezes the tab and corrupts the UI thread. We also cap iteration
   *  counts as belt-and-suspenders so a future bug in the bounds math
   *  can never lock the page. */
  function buildGrid(
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
    originX: number,
    originY: number,
    keyPrefix: string
  ): GridSet {
    const minor: GridLine[] = [];
    const major: GridLine[] = [];
    if (
      fit.scale === 0 ||
      !Number.isFinite(fit.scale) ||
      !Number.isFinite(minX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY) ||
      !Number.isFinite(originX) ||
      !Number.isFinite(originY)
    ) {
      return { minor, major };
    }
    const startKx = Math.ceil((minX - originX) / GRID_INCHES);
    const endKx = Math.floor((maxX - originX) / GRID_INCHES + 1e-3);
    const startKy = Math.ceil((minY - originY) / GRID_INCHES);
    const endKy = Math.floor((maxY - originY) / GRID_INCHES + 1e-3);
    const majorEvery = Math.round(MAJOR_GRID_INCHES / GRID_INCHES);
    for (let kx = startKx; kx <= endKx && kx - startKx <= MAX_GRID_LINES; kx++) {
      const x = originX + kx * GRID_INCHES;
      const p1 = fit.pointToPx({ x, y: minY });
      const p2 = fit.pointToPx({ x, y: maxY });
      const line: GridLine = {
        points: [p1.x, p1.y, p2.x, p2.y],
        key: `${keyPrefix}v-${kx}`,
      };
      (kx % majorEvery === 0 ? major : minor).push(line);
    }
    for (let ky = startKy; ky <= endKy && ky - startKy <= MAX_GRID_LINES; ky++) {
      const y = originY + ky * GRID_INCHES;
      const p1 = fit.pointToPx({ x: minX, y });
      const p2 = fit.pointToPx({ x: maxX, y });
      const line: GridLine = {
        points: [p1.x, p1.y, p2.x, p2.y],
        key: `${keyPrefix}h-${ky}`,
      };
      (ky % majorEvery === 0 ? major : minor).push(line);
    }
    return { minor, major };
  }

  /** Build the world-anchored "main" grid as three independent tiers so the
   *  caller can render each at its own opacity:
   *  - `major`  → every foot (always full opacity, the always-on reference)
   *  - `coarse` → at `coarseStep` inches, excluding any line already in `major`
   *  - `fine`   → at `fineStep` inches, excluding any line already in `coarse`
   *               or `major` (these are the lines that fade in during the
   *               crossfade between two zoom bands)
   *
   *  The function iterates at the finest step (`fineStep`) so every line we
   *  emit is consistently positioned, and classifies each by which tier it
   *  belongs to. This relies on each grid step being an integer divisor of
   *  the next-coarser step (enforced by `gridStepsFor`), so any line at a
   *  fineStep-multiple position that is *also* a coarseStep-multiple lands
   *  on the exact same pixel — the line gets emitted once into the heavier
   *  tier, and the seam between zoom bands stays invisible. */
  function buildAdaptiveGrid(
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
    originX: number,
    originY: number,
    coarseStep: number,
    fineStep: number,
    keyPrefix: string
  ): AdaptiveGridSet {
    const major: GridLine[] = [];
    const coarse: GridLine[] = [];
    const fine: GridLine[] = [];
    if (
      fit.scale === 0 ||
      !Number.isFinite(fit.scale) ||
      fineStep <= 0 ||
      !Number.isFinite(fineStep) ||
      !Number.isFinite(coarseStep) ||
      !Number.isFinite(minX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY) ||
      !Number.isFinite(originX) ||
      !Number.isFinite(originY)
    ) {
      return { major, coarse, fine };
    }
    const startKx = Math.ceil((minX - originX) / fineStep);
    const endKx = Math.floor((maxX - originX) / fineStep + 1e-3);
    const startKy = Math.ceil((minY - originY) / fineStep);
    const endKy = Math.floor((maxY - originY) / fineStep + 1e-3);
    for (let kx = startKx; kx <= endKx && kx - startKx <= MAX_GRID_LINES; kx++) {
      const x = originX + kx * fineStep;
      const offset = kx * fineStep;
      const isMajor = Math.abs(offset % MAJOR_GRID_INCHES) < 1e-6;
      const isCoarse = !isMajor && Math.abs(offset % coarseStep) < 1e-6;
      const p1 = fit.pointToPx({ x, y: minY });
      const p2 = fit.pointToPx({ x, y: maxY });
      const line: GridLine = {
        points: [p1.x, p1.y, p2.x, p2.y],
        key: `${keyPrefix}v-${kx}`,
      };
      if (isMajor) major.push(line);
      else if (isCoarse) coarse.push(line);
      else fine.push(line);
    }
    for (let ky = startKy; ky <= endKy && ky - startKy <= MAX_GRID_LINES; ky++) {
      const y = originY + ky * fineStep;
      const offset = ky * fineStep;
      const isMajor = Math.abs(offset % MAJOR_GRID_INCHES) < 1e-6;
      const isCoarse = !isMajor && Math.abs(offset % coarseStep) < 1e-6;
      const p1 = fit.pointToPx({ x: minX, y });
      const p2 = fit.pointToPx({ x: maxX, y });
      const line: GridLine = {
        points: [p1.x, p1.y, p2.x, p2.y],
        key: `${keyPrefix}h-${ky}`,
      };
      if (isMajor) major.push(line);
      else if (isCoarse) coarse.push(line);
      else fine.push(line);
    }
    return { major, coarse, fine };
  }

  /** Visible viewport in room coordinates, computed by inverting the Stage
   *  transform on the canvas's screen-space corners. The main grid is built
   *  across this rect (plus a viewport-sized buffer) so it appears infinite
   *  at any pan/zoom — without the buffer, smooth Konva-managed pans (which
   *  only sync `panZoom.x/y` back to React state on dragend) would briefly
   *  expose the grid's edge as the user drags. */
  const visibleRoomBounds = useMemo(() => {
    if (
      fit.scale === 0 ||
      !Number.isFinite(fit.scale) ||
      panZoom.scale === 0 ||
      !Number.isFinite(panZoom.scale) ||
      !Number.isFinite(panZoom.x) ||
      !Number.isFinite(panZoom.y) ||
      size.width === 0 ||
      size.height === 0
    ) {
      return { minX: bb.minX, minY: bb.minY, maxX: bb.maxX, maxY: bb.maxY };
    }
    // Stage is translated by (panZoom.x, panZoom.y) and scaled by panZoom.scale,
    // so the screen viewport [0,size]² maps to this rect in stage-local pixels:
    const stageLeft = -panZoom.x / panZoom.scale;
    const stageTop = -panZoom.y / panZoom.scale;
    const stageRight = (size.width - panZoom.x) / panZoom.scale;
    const stageBottom = (size.height - panZoom.y) / panZoom.scale;
    // fit.pxToRoom takes stage-local pixels (the inverse of fit.pointToPx).
    const tl = fit.pxToRoom({ x: stageLeft, y: stageTop });
    const br = fit.pxToRoom({ x: stageRight, y: stageBottom });
    const minX = Math.min(tl.x, br.x);
    const minY = Math.min(tl.y, br.y);
    const maxX = Math.max(tl.x, br.x);
    const maxY = Math.max(tl.y, br.y);
    // One full viewport of buffer on every side. This is enough headroom for
    // the user to drag-pan the canvas by ~one screenful before they outrun the
    // last-rendered grid; onDragEnd then re-syncs panZoom state and we
    // re-extend, so they never actually catch up to the edge in practice.
    const padX = maxX - minX;
    const padY = maxY - minY;
    return {
      minX: minX - padX,
      minY: minY - padY,
      maxX: maxX + padX,
      maxY: maxY + padY,
    };
  }, [fit, panZoom.x, panZoom.y, panZoom.scale, size.width, size.height, bb]);

  // Adaptive grid step: at MIN_PAN_SCALE we want 1' lines; at MAX_PAN_SCALE
  // we want 1" lines; in between we crossfade through clean integer-divisor
  // subdivisions ([12, 6, 3, 1]) so the user always sees a meaningful ruler.
  const gridStep = useMemo(() => gridStepsFor(panZoom.scale), [panZoom.scale]);
  /** Scale for annotation labels (wall lengths) — see `canvasLabels.ts`. */
  const labelK = annotationScale(panZoom.scale);

  // Main grid: world-anchored (origin 0,0), spans the entire visible viewport
  // (plus drag buffer) so it reads as an infinite reference grid no matter
  // where the user pans or how far they zoom out. Returned as three tiers
  // (major / coarse / fine) so the render pass can apply per-tier opacity.
  const mainGrid = useMemo<AdaptiveGridSet>(
    () =>
      buildAdaptiveGrid(
        visibleRoomBounds.minX,
        visibleRoomBounds.minY,
        visibleRoomBounds.maxX,
        visibleRoomBounds.maxY,
        0,
        0,
        gridStep.coarse,
        gridStep.fine,
        "main"
      ),
    // buildAdaptiveGrid closes over fit, so include it in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRoomBounds, fit, gridStep.coarse, gridStep.fine]
  );

  // Room internal grid: anchored to the room's own top-left so cells line up
  // with the walls. This is the grid the furniture snaps to; we clip it to the
  // polygon at render time so it only paints the room interior.
  const roomGrid = useMemo<GridSet>(
    () =>
      buildGrid(
        bb.minX,
        bb.minY,
        bb.maxX,
        bb.maxY,
        gridOrigin.x,
        gridOrigin.y,
        "room"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bb, fit, gridOrigin]
  );

  // Render walls as thick black lines with labels
  const wallSegments = useMemo(() => {
    return points.map((p, i) => {
      const next = points[(i + 1) % points.length];
      const len = distance(p, next);
      const m = midpoint(p, next);
      const angle = wallAngleDeg(p, next);
      return { a: p, b: next, len, mid: m, angle, index: i };
    });
  }, [points]);

  function applyFurnitureDrag(id: string, pointerRoom: Vec2, node: Konva.Node) {
    const item = furniture.find((f) => f.id === id);
    if (!item) return;
    const session = furnitureDragRef.current;
    if (session && session.id === id) {
      session.lastPointerRoom = pointerRoom;
      session.node = node;
    }

    let next = pointerRoom;
    let lockX = false;
    let lockY = false;
    const origin = session && session.id === id ? session.origin : null;
    if (shiftHeldRef.current && origin) {
      next = lockToDominantAxis(origin, pointerRoom);
      // Horizontal (or tied) → freeze Y; vertical → freeze X.
      lockY = Math.abs(pointerRoom.x - origin.x) >= Math.abs(pointerRoom.y - origin.y);
      lockX = !lockY;
    }

    const footprint = resolveFurniture(item).footprint;
    if (snapEnabled) {
      next = snapPolygonToGrid(next, footprint, item.rotation, gridSnapInches, gridOrigin);
      if (origin && lockX) next = { x: origin.x, y: next.y };
      if (origin && lockY) next = { x: next.x, y: origin.y };
    }
    if (furnitureSnapEnabled) {
      const others = furniture
        .filter((f) => f.id !== id)
        .map((f) => ({
          center: { x: f.x, y: f.y },
          localOutline: resolveFurniture(f).footprint,
          rotationDeg: f.rotation,
        }));
      next = snapPolygonToFurniture(
        next,
        footprint,
        item.rotation,
        others,
        FURNITURE_SNAP_INCHES,
        { x: lockX, y: lockY }
      );
      if (origin && lockX) next = { x: origin.x, y: next.y };
      if (origin && lockY) next = { x: next.x, y: origin.y };
    }
    next = clampPoseInRoom(next, footprint, item.rotation, room);
    const px = fit.pointToPx(next);
    node.position({ x: px.x, y: px.y });
    // Keep the selection grips with the piece. The store (and everything
    // subscribed to it: the inspector, localStorage, the other pieces)
    // stays put until the pointer goes up.
    if (session && session.id === id) session.pose = next;
    const handles = node.getStage()?.findOne(".furniture-handles");
    if (handles) handles.position(px);
    if (sharedRoomId) {
      setDragging({
        kind: "furniture",
        id,
        x: next.x,
        y: next.y,
        rotation: item.rotation,
      });
    }
  }

  function pieceLocalFromStage(stage: Konva.Stage, f: Furniture): Vec2 | null {
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    // getPointerPosition is already in stage pixels, the same space as
    // fit.pointToPx. Undo the piece's rotation, then convert to inches.
    const center = fit.pointToPx({ x: f.x, y: f.y });
    const localPx = roomToPieceLocal(pointer, center, f.rotation);
    const scale = fit.scale || 1;
    return { x: localPx.x / scale, y: localPx.y / scale };
  }

  /** Local inches of a pointer that landed on this piece's sink, or null. */
  function sinkUnderPointer(f: Furniture, stage: Konva.Stage): Vec2 | null {
    const model = f.model;
    if (!model || model.kind !== "parametric") return null;
    if (model.template !== "counter" && model.template !== "island") return null;
    const local = pieceLocalFromStage(stage, f);
    if (!local) return null;
    if (!pointHitsSink(resolveFurniture(f).parts, local)) return null;
    return local;
  }

  function startSinkDrag(f: Furniture, local: Vec2, stage: Konva.Stage) {
    const track = sinkTrackFor(f);
    if (!track || f.model?.kind !== "parametric") return;
    const stored = f.model.params.sinkAt;
    const current =
      typeof stored === "number" && stored >= 0 ? Math.round(stored) : track.autoPermille;
    const grab = track.project(local) - current;
    sinkDragRef.current = { id: f.id };
    sinkArmRef.current = null;
    cloneOnDragRef.current = false;
    setSinkDraggingId(f.id);
    beginGesture();
    stage.container().style.cursor = "grabbing";

    const piece = { x: f.x, y: f.y, rotation: f.rotation };
    let from = current;
    let last = current;
    let dirty = false;

    const move = (ev: PointerEvent) => {
      if (sinkDragRef.current?.id !== f.id) return;
      stage.setPointersPositions(ev);
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      const center = fit.pointToPx(piece);
      const localPx = roomToPieceLocal(pointer, center, piece.rotation);
      const scale = fit.scale || 1;
      const at = { x: localPx.x / scale, y: localPx.y / scale };
      const next = track.clampPermille(from, track.project(at) - grab);
      from = next;
      if (!dirty && next === current) return;
      dirty = true;
      if (next === last) return;
      last = next;
      const item = useDesign.getState().design.furniture.find((p) => p.id === f.id);
      if (!item || item.model?.kind !== "parametric") return;
      updateFurniture(f.id, {
        model: { ...item.model, params: { ...item.model.params, sinkAt: next } },
      });
    };
    let closed = false;
    const up = () => {
      if (closed) return;
      closed = true;
      stopSinkListeners.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      ignoreFurnitureDragEnd.current = f.id;
      sinkDragRef.current = null;
      sinkDragEndedAt.current = Date.now();
      setSinkDraggingId(null);
      endGesture();
      stage.container().style.cursor = "default";
    };
    stopSinkListeners.current = up;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  function handleFurnitureDragMove(e: KonvaEventObject<DragEvent>, id: string) {
    const node = e.target;
    const native = e.evt as { shiftKey?: boolean } | undefined;
    if (native && typeof native.shiftKey === "boolean") {
      shiftHeldRef.current = native.shiftKey;
    }
    const roomPos = fit.pxToRoom({ x: node.x(), y: node.y() });
    applyFurnitureDrag(id, roomPos, node);
  }

  function handleFurnitureDragStart(e: KonvaEventObject<DragEvent>, id: string) {
    if (sinkDragRef.current?.id === id) {
      const node = e.target;
      node.stopDrag();
      const item = furniture.find((piece) => piece.id === id);
      if (item) {
        const c = fit.pointToPx({ x: item.x, y: item.y });
        node.position(c);
      }
      return;
    }
    if (!interactive) return;
    beginGesture({ blockUndo: true });
    if (cloneOnDragRef.current) {
      cloneOnDragRef.current = false;
      const src = furniture.find((f) => f.id === id);
      if (src) {
        addFurniture(payloadWithoutId(src), { select: false, source: "duplicate" });
      }
    }
    const item = furniture.find((f) => f.id === id);
    const native = e.evt as { shiftKey?: boolean } | undefined;
    shiftHeldRef.current = Boolean(native?.shiftKey);
    if (item) {
      furnitureDragRef.current = {
        id,
        origin: { x: item.x, y: item.y },
        lastPointerRoom: { x: item.x, y: item.y },
        pose: null,
        node: e.target,
      };
    }
    selectFurniture(id);
    if (sharedRoomId) {
      setDraggingFurniture(id);
      if (item) {
        setDragging({
          kind: "furniture",
          id,
          x: item.x,
          y: item.y,
          rotation: item.rotation,
        });
      }
    }
  }

  function handleFurnitureDragEnd(id: string) {
    if (sinkDragRef.current?.id === id || ignoreFurnitureDragEnd.current === id) {
      ignoreFurnitureDragEnd.current = null;
      return;
    }
    const drag = furnitureDragRef.current;
    if (drag?.id === id && drag.pose) {
      updateFurniture(id, { x: drag.pose.x, y: drag.pose.y });
    }
    const item = useDesign.getState().design.furniture.find((f) => f.id === id) ?? null;
    const presetName = furnitureMovedName(
      drag?.id === id ? drag.origin : null,
      item
    );
    cloneOnDragRef.current = false;
    furnitureDragRef.current = null;
    shiftHeldRef.current = false;
    pickup.drop();
    endGesture();
    if (presetName) track("furniture_moved", { preset_name: presetName, source: "user" });
    if (presetName && adjustingId === id) noteAdjustChange();
    if (!sharedRoomId) return;
    setDraggingFurniture(null);
    setDragging(null);
  }

  const adjusting = Boolean(selectedFurniture && adjustingId === selectedFurniture.id);

  useEffect(() => {
    if (!adjusting || !selectedFurniture) return;
    const b = boundingBox(resolveFurniture(selectedFurniture).footprint);
    const piecePx = Math.min(b.width, b.height) * fit.scale * panZoom.scale;
    track("furniture_adjust_mode_entered", { piece_px: Math.round(piecePx) });
    // Once per entry, not on every size change while adjusting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustingId]);

  /** Canvas-px box around the Adjust knob ring, knobs and rotation knob included. */
  function adjustRingBox(f: Furniture) {
    const scale = Math.max(panZoom.scale, 1e-6);
    const footprint = boundingBox(resolveFurniture(f).footprint);
    const ring = adjustRingLayout(footprint.width * fit.scale * scale, footprint.height * fit.scale * scale);
    const knob = ADJUST_RING.knobHit / 2;
    const corners = [
      { x: -ring.halfX - knob, y: ring.knobs.rotate.y - knob },
      { x: ring.halfX + knob, y: ring.knobs.rotate.y - knob },
      { x: ring.halfX + knob, y: ring.halfY + knob },
      { x: -ring.halfX - knob, y: ring.halfY + knob },
    ].map((p) => ({ x: p.x / scale, y: p.y / scale }));
    const c = fit.pointToPx({ x: f.x, y: f.y });
    return boundingBox(rotate(corners, f.rotation).map((p) => ({ x: p.x + c.x, y: p.y + c.y })));
  }

  // When the details sheet opens (or grows) over the selected piece, or Adjust
  // mode puts its knob ring around it, glide the canvas just enough to bring
  // it all back into the uncovered area. Keyed on what's covered and what's
  // selected, not on every drag frame.
  useEffect(() => {
    if (!interactive || !selectedFurniture) return;
    if (!adjusting && inspector.bottom <= 0 && inspector.right <= 0) return;
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const outline = rotate(resolveFurniture(selectedFurniture).footprint, selectedFurniture.rotation).map(
      (p) => fit.pointToPx({ x: p.x + selectedFurniture.x, y: p.y + selectedFurniture.y })
    );
    const box = adjusting
      ? adjustRingBox(selectedFurniture)
      : (() => {
          const b = boundingBox(outline);
          const grip = ROTATE_GRIP_ALLOWANCE_PX / Math.max(panZoom.scale, 1e-6);
          return { minX: b.minX - grip, minY: b.minY - grip, width: b.width + grip * 2, height: b.height + grip * 2 };
        })();
    panZoom.ensureVisible(
      { x: box.minX, y: box.minY, width: box.width, height: box.height },
      {
        top: chrome.insets.top,
        left: chrome.insets.left,
        bottom: Math.max(chrome.insets.bottom, inspector.bottom - (window.innerHeight - r.bottom)),
        right: Math.max(chrome.insets.right, inspector.right - (window.innerWidth - r.right)),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, inspector.bottom, inspector.right, adjusting]);

  /** A second finger landed: throw away whatever the first finger was editing. */
  function handlePinchInterrupt() {
    pickup.disarm();
    pickup.drop();
    if (sinkDragRef.current) {
      cancelGesture();
      stopSinkListeners.current?.();
    }
    const drag = furnitureDragRef.current;
    if (drag) {
      ignoreFurnitureDragEnd.current = drag.id;
      const px = fit.pointToPx(drag.origin);
      drag.node.position(px);
      const handles = drag.node.getStage()?.findOne(".furniture-handles");
      if (handles) handles.position(px);
      drag.node.stopDrag();
      furnitureDragRef.current = null;
      cloneOnDragRef.current = false;
      shiftHeldRef.current = false;
      cancelGesture();
      if (sharedRoomId) {
        setDraggingFurniture(null);
        setDragging(null);
      }
    }
    if (personDragRef.current) {
      personDragRef.current = false;
      cancelGesture();
    }
  }

  function broadcastCursorFromStage(stage: Konva.Stage | null) {
    if (!sharedRoomId || !stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    // Pointer is in CSS pixels relative to the Stage; account for the
    // Stage's own pan/zoom by inverting the transform via Konva.
    const transform = stage.getAbsoluteTransform().copy().invert();
    const local = transform.point(pointer);
    const roomPos = fit.pxToRoom(local);
    setCursor({
      view: "design",
      wallIndex: null,
      x: roomPos.x,
      y: roomPos.y,
    });
  }

  function handleStageMouseMove(e: KonvaEventObject<MouseEvent>) {
    broadcastCursorFromStage(e.target.getStage());
  }

  // Re-broadcast cursor after wheel zoom: the user hasn't moved the
  // mouse but their cursor's *room* position changed (different scale
  // → different room point under the same screen point). Mirrors the
  // Figma behavior of always reporting the latest room coordinate.
  function handleStageWheelCursor(e: KonvaEventObject<WheelEvent>) {
    broadcastCursorFromStage(e.target.getStage());
  }

  // While the user pans the Stage (draggable Stage), Konva fires
  // dragMove on the Stage itself. Broadcasting the cursor here keeps
  // collaborators' view of where this user is hovering in sync as the
  // room slides under the pointer.
  function handleStageDragMoveCursor(e: KonvaEventObject<DragEvent>) {
    broadcastCursorFromStage(e.target.getStage());
  }

  function handleStageMouseLeave() {
    if (!sharedRoomId) return;
    setCursor(null);
  }

  const [hoverWall, setHoverWall] = useState<number | string | null>(null);

  function handleWallClick(i: number) {
    setActiveWallIndex(i);
    setView("wall");
  }

  function handleInteriorWallClick(wallId: string, segment: number) {
    useDesign.getState().setActiveInteriorWall({ wallId, segment });
    setView("wall");
  }

  /** Wall a touch has highlighted. The pill chooses Shape or the elevation;
   *  a second tap on the same wall does not pick for them. */
  const [armedWall, setArmedWall] = useState<{
    key: string;
    mid: Vec2;
    open: () => void;
    move: () => void;
  } | null>(null);

  /** Touch taps on a wall take a confirm step (see `confirmWallOpen`). */
  function handleWallTap(
    key: string,
    mid: Vec2,
    actions: { open: () => void; move: () => void },
  ) {
    const intent = wallTapIntent(armedWall?.key ?? null, key, confirmWallOpen);
    if (intent === "open") {
      actions.open();
      return;
    }
    if (intent === "keep") return;
    setArmedWall({ key, mid, ...actions });
  }

  /** Shape, with this wall selected so its length can be set or dragged. */
  function moveOutlineWall(index: number) {
    const { setView, setBuildTool, selectRoomWall } = useDesign.getState();
    setView("build");
    setBuildTool("edit");
    selectRoomWall(index);
  }

  function moveInteriorWall(wallId: string, segment: number) {
    const { setView, setBuildTool, selectInteriorWall } = useDesign.getState();
    setView("build");
    setBuildTool("edit");
    selectInteriorWall({ wallId, segment });
  }

  const interiorWalls = useMemo(() => interiorSegments(room), [room]);

  // Mark mount positions on top-down view (so design view shows where TVs are mounted)
  const mountMarkers = useMemo(
    () =>
      mounts.flatMap((m) => {
        const seg = mountOnWall(m, room);
        return seg ? [seg] : [];
      }),
    [mounts, points]
  );

  // Endpoints of each opening along its wall, plus the inward normal so
  // doors can draw a swing that opens into the room.
  const openingMarkers = useMemo(
    () =>
      openings.flatMap((o) => {
        const seg = openingOnWall(o, room);
        return seg ? [seg] : [];
      }),
    [openings, points]
  );

  function clearStageSelection(e: KonvaEventObject<unknown>) {
    if (!interactive || measureActive) return;
    if (e.target !== e.target.getStage()) return;
    setArmedWall(null);
    // Block empty-stage deselection while an unnamed (just-placed custom)
    // piece is selected — the inspector's required-name treatment is what
    // forces a label, and letting users tap away would leave anonymous
    // boxes scattered on the canvas. The inspector still lets them
    // explicitly discard via its trash affordance.
    if (selectedId) {
      const sel = furniture.find((f) => f.id === selectedId);
      if (sel && !sel.name.trim()) return;
    }
    selectFurniture(null);
  }

  function stableCenter(id: string, px: { x: number; y: number }) {
    const prev = centerCacheRef.current.get(id);
    if (prev && prev.x === px.x && prev.y === px.y) return prev;
    centerCacheRef.current.set(id, px);
    return px;
  }

  furnitureHandlersRef.current = {
    onTouchStart(e) {
      const id = furnitureIdFromEvent(e);
      const f = furniture.find((piece) => piece.id === id);
      if (!f) return;
      const canDrag =
        interactive &&
        !measureActive &&
        editingModulesId !== f.id &&
        sinkDraggingId !== f.id &&
        !(adjustingId && adjustingId !== f.id);
      const immediate = dragsImmediately({ coarse, selected: selectedId === f.id });
      if (!canDrag || immediate) return;
      const touch = pressedTouch(e);
      if (!touch) return;
      const node = e.currentTarget;
      const evt = e.evt;
      pickup.arm(f.id, touch, {
        onPickup: () => startNodePickup(node, touch, evt),
      });
    },
    onMouseMove(e) {
      const id = furnitureIdFromEvent(e);
      const f = furniture.find((piece) => piece.id === id);
      if (!f) return;
      if (!interactive || measureActive || sinkDraggingId) return;
      const stage = e.target.getStage();
      if (!stage) return;
      const over = Boolean(selectedId === f.id && sinkUnderPointer(f, stage));
      setSinkHoverId((cur) => {
        const next = over ? f.id : cur === f.id ? null : cur;
        return next === cur ? cur : next;
      });
      stage.container().style.cursor = over ? "move" : "grab";
    },
    onMouseLeave(e) {
      const id = furnitureIdFromEvent(e);
      setSinkHoverId((cur) => (cur === id ? null : cur));
    },
    onDblClick(e) {
      const id = furnitureIdFromEvent(e);
      const f = furniture.find((piece) => piece.id === id);
      if (!f) return;
      if (interactive && f.model?.kind === "modular") setEditingModules(f.id);
    },
    onDblTap(e) {
      const id = furnitureIdFromEvent(e);
      const f = furniture.find((piece) => piece.id === id);
      if (!f) return;
      if (interactive && f.model?.kind === "modular") {
        setEditingModules(f.id);
        return;
      }
      if (!interactive || measureActive) return;
      if (Date.now() - sinkDragEndedAt.current < 400) return;
      const stage = e.target.getStage();
      if (!stage || !sinkUnderPointer(f, stage)) return;
      sinkArmRef.current = f.id;
    },
    onMouseDown(e) {
      const id = furnitureIdFromEvent(e);
      const f = furniture.find((piece) => piece.id === id);
      if (!f) return;
      if (!interactive || measureActive) return;
      const stage = e.target.getStage();
      const primary = e.evt.button === 0;
      const local = primary && stage ? sinkUnderPointer(f, stage) : null;
      const armed = sinkArmRef.current === f.id;
      if (local && stage && (e.evt.detail >= 2 || armed)) {
        selectFurniture(f.id);
        startSinkDrag(f, local, stage);
        return;
      }
      if (!local) sinkArmRef.current = null;
      ignoreFurnitureDragEnd.current = null;
      cloneOnDragRef.current = e.evt.altKey;
      selectFurniture(f.id);
    },
    onContextMenu(e) {
      const id = furnitureIdFromEvent(e);
      if (!id || !interactive || !onFurnitureContextMenu) return;
      e.evt.preventDefault();
      e.cancelBubble = true;
      selectFurniture(id);
      onFurnitureContextMenu(id, {
        x: e.evt.clientX,
        y: e.evt.clientY,
      });
    },
    onTap(e) {
      const id = furnitureIdFromEvent(e);
      if (!id || !interactive || measureActive) return;
      setArmedWall(null);
      selectFurniture(id);
    },
    onDragStart(e) {
      const id = furnitureIdFromEvent(e);
      if (!id) return;
      handleFurnitureDragStart(e, id);
    },
    onDragMove(e) {
      const id = furnitureIdFromEvent(e);
      if (!id || !interactive || measureActive) return;
      handleFurnitureDragMove(e, id);
    },
    onDragEnd(e) {
      const id = furnitureIdFromEvent(e);
      if (!id) return;
      handleFurnitureDragEnd(id);
    },
  };

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 touch-none select-none${measureActive ? " cursor-crosshair" : ""}`}
      // Suppress the browser-native context menu over the canvas so we can
      // use right-click to pop our own furniture menu without the OS one
      // racing it.
      onContextMenu={
        onFurnitureContextMenu ? (e) => e.preventDefault() : undefined
      }
    >
      {chrome.probes}
      {size.width > 0 && size.height > 0 && (
        <Stage
          ref={panZoom.stageRef}
          width={size.width}
          height={size.height}
          scaleX={panZoom.scale}
          scaleY={panZoom.scale}
          x={panZoom.x}
          y={panZoom.y}
          draggable={!measureActive}
          dragDistance={coarse ? SLOP_PX : undefined}
          onDragStart={(e) => {
            if (e.target === e.target.getStage()) setArmedWall(null);
          }}
          onDragEnd={panZoom.onDragEnd}
          onDragMove={handleStageDragMoveCursor}
          onWheel={(e) => {
            panZoom.onWheel(e);
            handleStageWheelCursor(e);
          }}
          onTouchStart={panZoom.onTouchStart}
          onTouchMove={(e) => {
            panZoom.onTouchMove(e);
          }}
          onTouchEnd={panZoom.onTouchEnd}
          onClick={clearStageSelection}
          onTap={clearStageSelection}
          onMouseMove={handleStageMouseMove}
          onMouseLeave={handleStageMouseLeave}
        >
          <Layer listening={false}>
            {/* Main grid: subtle gray reference grid in world coordinates,
                rendered as three tiers so subdivisions can crossfade with
                zoom — fine first (lightest, fades in) → coarse → major (the
                "every foot" reference, always on top). Painted across the
                full visible viewport (plus drag buffer) so it appears
                infinite at any pan/zoom. */}
            {mainGrid.fine.map((g) => (
              <Line
                key={g.key}
                points={g.points}
                stroke={MAIN_GRID_FINE}
                strokeWidth={1}
                opacity={gridStep.fineOpacity}
                listening={false}
              />
            ))}
            {mainGrid.coarse.map((g) => (
              <Line
                key={g.key}
                points={g.points}
                stroke={MAIN_GRID_COARSE}
                strokeWidth={1}
                listening={false}
              />
            ))}
            {mainGrid.major.map((g) => (
              <Line
                key={g.key}
                points={g.points}
                stroke={MAIN_GRID_MAJOR}
                strokeWidth={1}
                listening={false}
              />
            ))}

            {/* Room internal grid: anchored to the room's own origin and
                clipped to the room polygon, so it visibly fills only the room
                interior. This is the grid furniture snaps to, so we tint it in
                the accent color and only show it while snap is enabled. */}
            {snapEnabled && (
              <Group
                clipFunc={(ctx) => {
                  ctx.beginPath();
                  for (let i = 0; i < points.length; i++) {
                    const p = fit.pointToPx(points[i]);
                    if (i === 0) ctx.moveTo(p.x, p.y);
                    else ctx.lineTo(p.x, p.y);
                  }
                  ctx.closePath();
                }}
              >
                {roomGrid.minor.map((g) => (
                  <Line
                    key={g.key}
                    points={g.points}
                    stroke={ROOM_GRID_MINOR}
                    strokeWidth={1}
                    listening={false}
                  />
                ))}
                {roomGrid.major.map((g) => (
                  <Line
                    key={g.key}
                    points={g.points}
                    stroke={ROOM_GRID_MAJOR}
                    strokeWidth={1}
                    listening={false}
                  />
                ))}
              </Group>
            )}
          </Layer>

          <Layer>
            {/* Room fill (a faint tint of the chosen floor) */}
            <Line
              points={flatPolygon}
              closed
              fill={floorFill}
              stroke="#0c0c10"
              strokeWidth={WALL_THICKNESS_PX}
              lineJoin="round"
              shadowColor="#000"
              shadowBlur={20}
              shadowOpacity={0.06}
              shadowOffsetY={4}
            />
            {interiorWalls.map((w) => {
              const a = fit.pointToPx(w.a);
              const b = fit.pointToPx(w.b);
              return (
                <Line
                  key={`iw-${w.wallId}-${w.segment}`}
                  points={[a.x, a.y, b.x, b.y]}
                  stroke="#0c0c10"
                  strokeWidth={WALL_THICKNESS_PX}
                  lineCap="square"
                  listening={false}
                />
              );
            })}

            {/* Wall labels */}
            {showWallLabels && !demoActive &&
              wallSegments.map((w) => {
                const label = formatLength(w.len, unit);
                // Estimate text width to render bg
                const charW = 7;
                const textWidth = label.length * charW + 14;
                // Sit the pill just outside the wall, on the side away from
                // the room, so it never covers a piece pushed against that
                // wall (beds, dressers and their names live right there).
                const aPx = fit.pointToPx(w.a);
                const bPx = fit.pointToPx(w.b);
                const mPx = fit.pointToPx(w.mid);
                const cPx = fit.pointToPx(roomCentroid);
                const dx = bPx.x - aPx.x;
                const dy = bPx.y - aPx.y;
                const lenPx = Math.hypot(dx, dy) || 1;
                let nx = -dy / lenPx;
                let ny = dx / lenPx;
                if ((mPx.x - cPx.x) * nx + (mPx.y - cPx.y) * ny < 0) {
                  nx = -nx;
                  ny = -ny;
                }
                const off = 15 * labelK;
                return (
                  <Group
                    key={`wl-${w.index}`}
                    x={mPx.x + nx * off}
                    y={mPx.y + ny * off}
                    scaleX={labelK}
                    scaleY={labelK}
                  >
                    <Rect
                      x={-textWidth / 2}
                      y={-11}
                      width={textWidth}
                      height={22}
                      fill="#0c0c10"
                      cornerRadius={11}
                    />
                    <Text
                      text={label}
                      x={-textWidth / 2}
                      y={-7}
                      width={textWidth}
                      align="center"
                      fontSize={12}
                      fontStyle="600"
                      fontFamily="Inter, sans-serif"
                      fill="#ffffff"
                    />
                  </Group>
                );
              })}

            {/* Wall click hit-areas (for wall view) */}
            {enableWallSelect &&
              !measureActive &&
              wallSegments.map((w) => {
                const aPx = fit.pointToPx(w.a);
                const bPx = fit.pointToPx(w.b);
                const isHover = hoverWall === w.index;
                const isActive = activeInteriorWall == null && activeWallIndex === w.index;
                const armKey = `p:${w.index}`;
                const lit = isHover || isActive || armedWall?.key === armKey;
                return (
                  <Line
                    key={`wh-${w.index}`}
                    points={[aPx.x, aPx.y, bPx.x, bPx.y]}
                    stroke={lit ? "#2a70f5" : "rgba(0,0,0,0)"}
                    strokeWidth={WALL_THICKNESS_PX + 14}
                    lineCap="round"
                    hitStrokeWidth={WALL_THICKNESS_PX + 24}
                    onMouseEnter={(e) => {
                      setHoverWall(w.index);
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "pointer";
                    }}
                    onMouseLeave={(e) => {
                      setHoverWall(null);
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "default";
                    }}
                    onClick={() => handleWallClick(w.index)}
                    onTap={() =>
                      handleWallTap(armKey, w.mid, {
                        open: () => handleWallClick(w.index),
                        move: () => moveOutlineWall(w.index),
                      })
                    }
                    opacity={lit ? 0.4 : 1}
                  />
                );
              })}
            {enableWallSelect &&
              !measureActive &&
              interiorWalls.map((w) => {
                const aPx = fit.pointToPx(w.a);
                const bPx = fit.pointToPx(w.b);
                const hoverKey = `i:${w.wallId}:${w.segment}`;
                const isHover = hoverWall === hoverKey;
                const isActive =
                  activeInteriorWall?.wallId === w.wallId &&
                  activeInteriorWall?.segment === w.segment;
                const lit = isHover || isActive || armedWall?.key === hoverKey;
                return (
                  <Line
                    key={`iwh-${w.wallId}-${w.segment}`}
                    points={[aPx.x, aPx.y, bPx.x, bPx.y]}
                    stroke={lit ? "#2a70f5" : "rgba(0,0,0,0)"}
                    strokeWidth={WALL_THICKNESS_PX + 14}
                    lineCap="round"
                    hitStrokeWidth={WALL_THICKNESS_PX + 24}
                    onMouseEnter={(e) => {
                      setHoverWall(hoverKey);
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "pointer";
                    }}
                    onMouseLeave={(e) => {
                      setHoverWall(null);
                      const stage = e.target.getStage();
                      if (stage) stage.container().style.cursor = "default";
                    }}
                    onClick={() => handleInteriorWallClick(w.wallId!, w.segment!)}
                    onTap={() =>
                      handleWallTap(hoverKey, midpoint(w.a, w.b), {
                        open: () => handleInteriorWallClick(w.wallId!, w.segment!),
                        move: () => moveInteriorWall(w.wallId!, w.segment!),
                      })
                    }
                    opacity={lit ? 0.4 : 1}
                  />
                );
              })}

            {/* Furniture — each piece is a parametric assembly of parts (see
                 FurnitureNode). Geometry, materials and the label all derive
                 from `resolveFurniture`, so this list only wires events. */}
            {furniture.map((f) => {
              const live = furnitureDragRef.current;
              const at = live?.id === f.id && live.pose ? live.pose : { x: f.x, y: f.y };
              const center = stableCenter(f.id, fit.pointToPx(at));
              const isSelected = selectedId === f.id;
              // Briefly glow when the History sheet's "Highlight on canvas"
              // points at this piece. Stays in sync with `flashFurnitureIds`'s
              // auto-clear timer in the store.
              const isFlashed = flashedIds.includes(f.id);
              // While another piece is in Adjust mode this one fades back and
              // only answers a tap (which selects it and leaves the mode).
              const behindAdjust = adjustingId !== null && adjustingId !== f.id;
              const canDrag =
                interactive &&
                !measureActive &&
                editingModulesId !== f.id &&
                sinkDraggingId !== f.id &&
                !behindAdjust;
              const immediate = dragsImmediately({ coarse, selected: isSelected });
              const lifted = pickup.liftedKey === f.id;
              return (
                <FurnitureNode
                  key={f.id}
                  furniture={f}
                  pxPerIn={fit.scale}
                  center={center}
                  stageScale={panZoom.scale}
                  isSelected={isSelected}
                  isFlashed={isFlashed}
                  draggable={canDrag && (immediate || lifted)}
                  dragDistance={coarse ? TOUCH_DRAG_DISTANCE : undefined}
                  lifted={lifted}
                  dimmed={behindAdjust}
                  holdCursor={sinkDraggingId !== null}
                  onTouchStart={nodeEvents.onTouchStart}
                  onMouseMove={nodeEvents.onMouseMove}
                  onMouseLeave={nodeEvents.onMouseLeave}
                  onDblClick={nodeEvents.onDblClick}
                  onDblTap={nodeEvents.onDblTap}
                  onMouseDown={nodeEvents.onMouseDown}
                  onContextMenu={nodeEvents.onContextMenu}
                  onTap={nodeEvents.onTap}
                  onDragStart={nodeEvents.onDragStart}
                  onDragMove={nodeEvents.onDragMove}
                  onDragEnd={nodeEvents.onDragEnd}
                />
              );
            })}

            {/* Wall mounts: draw small slivers on top of walls indicating mounted item */}
            {mountMarkers.map((m) => {
              if (m.len === 0) return null;
              const sPx = fit.pointToPx(m.start);
              const ePx = fit.pointToPx(m.end);
              return (
                <Line
                  key={`mm-${m.id}`}
                  points={[sPx.x, sPx.y, ePx.x, ePx.y]}
                  stroke={m.color}
                  strokeWidth={WALL_THICKNESS_PX + 4}
                  lineCap="round"
                />
              );
            })}

            {/* Openings (doors + windows): draw a colored gap where the wall
                opens, plus a quarter-circle swing arc for hinged doors.
                Wall cutouts get short jamb ticks and no leaf; sliding doors
                get two overlapping panels along the wall. */}
            {openingMarkers.map((o) => {
              if (o.len === 0) return null;
              const sPx = fit.pointToPx(o.start);
              const ePx = fit.pointToPx(o.end);
              const widthPx = fit.inToPx(o.width);
              // Cover the wall stroke with a "cut-out" stroke in the room fill
              // color so the opening visibly breaks the wall.
              const stroke = o.kind === "door" ? "#ffffff" : "#cfe2f3";
              const swingRadiusPx = o.swings ? widthPx : 0;
              // The swing arc goes from the hinge (start of opening) outward
              // along the inward normal. Hinge side picks whichever end of the
              // opening is closer to the wall's vertex 'a' so it consistently
              // anchors to the start of the wall.
              const hinge = sPx;
              const inwardEnd = fit.pointToPx({
                x: o.start.x + o.nx * o.width,
                y: o.start.y + o.ny * o.width,
              });
              const jambA = fit.pointToPx({
                x: o.start.x + o.nx * CUTOUT_JAMB_IN,
                y: o.start.y + o.ny * CUTOUT_JAMB_IN,
              });
              const jambB = fit.pointToPx({
                x: o.end.x + o.nx * CUTOUT_JAMB_IN,
                y: o.end.y + o.ny * CUTOUT_JAMB_IN,
              });
              const tipEnd = ePx;
              return (
                <Group key={`op-${o.id}`}>
                  {/* The cut-out gap covering the wall stroke */}
                  <Line
                    points={[sPx.x, sPx.y, ePx.x, ePx.y]}
                    stroke={stroke}
                    strokeWidth={WALL_THICKNESS_PX + 2}
                    lineCap="butt"
                  />
                  {/* Windows: a soft blue band so they read distinctly from doors */}
                  {o.kind === "window" && (
                    <Line
                      points={[sPx.x, sPx.y, ePx.x, ePx.y]}
                      stroke="#5b9bd5"
                      strokeWidth={3}
                      lineCap="butt"
                    />
                  )}
                  {/* Sliding door: a panel on each face of the track,
                      overlapping at the middle */}
                  {o.slides &&
                    slidingPanels(o).map((p, i) => {
                      const a = fit.pointToPx(p.start);
                      const b = fit.pointToPx(p.end);
                      // Each panel fills one half of the opening's gap.
                      const d = (WALL_THICKNESS_PX / 2 + 1) * p.side;
                      return (
                        <Line
                          key={i}
                          points={[
                            a.x, a.y, b.x, b.y,
                            b.x + o.nx * d, b.y + o.ny * d,
                            a.x + o.nx * d, a.y + o.ny * d,
                          ]}
                          closed
                          fill="#ffffff"
                          stroke="#0c0c10"
                          strokeWidth={1.25}
                        />
                      );
                    })}
                  {/* Wall cutout: casing jambs, no leaf */}
                  {o.kind === "door" && !o.swings && !o.slides && (
                    <>
                      <Line
                        points={[sPx.x, sPx.y, jambA.x, jambA.y]}
                        stroke="#0c0c10"
                        strokeWidth={1.5}
                        lineCap="butt"
                      />
                      <Line
                        points={[ePx.x, ePx.y, jambB.x, jambB.y]}
                        stroke="#0c0c10"
                        strokeWidth={1.5}
                        lineCap="butt"
                      />
                    </>
                  )}
                  {/* Doors: swing arc + door leaf */}
                  {o.swings && swingRadiusPx > 0 && (
                    <>
                      <Line
                        points={[hinge.x, hinge.y, inwardEnd.x, inwardEnd.y]}
                        stroke="#0c0c10"
                        strokeWidth={1.5}
                      />
                      <Path
                        data={arcPath(hinge, inwardEnd, tipEnd, swingRadiusPx)}
                        stroke="#0c0c10"
                        strokeWidth={1}
                        dash={[4, 4]}
                      />
                    </>
                  )}
                </Group>
              );
            })}

            {/* Person silhouette (top-down) */}
            {!hidePerson && (
              <PersonOverlay
                positionPx={fit.pointToPx(personPos)}
                shoulderPx={fit.inToPx(shoulderIn)}
                draggable={
                  interactive &&
                  !measureActive &&
                  (dragsImmediately({ coarse, selected: false }) || pickup.liftedKey === "person")
                }
                dragDistance={coarse ? TOUCH_DRAG_DISTANCE : undefined}
                onTouchStart={(e) => {
                  if (!interactive || measureActive || !coarse) return;
                  const touch = pressedTouch(e);
                  if (!touch) return;
                  const node = e.currentTarget;
                  const evt = e.evt;
                  pickup.arm("person", touch, {
                    onPickup: () => startNodePickup(node, touch, evt),
                  });
                }}
                onDragStart={() => {
                  personDragRef.current = true;
                  beginGesture({ blockUndo: true });
                }}
                onDragMove={(p) => {
                  if (!personDragRef.current) return;
                  const room = fit.pxToRoom(p);
                  const clamped = clampInside(room, points);
                  setPerson({ position: clamped });
                }}
                onDragEnd={() => {
                  pickup.drop();
                  if (!personDragRef.current) return;
                  personDragRef.current = false;
                  endGesture();
                }}
              />
            )}

            {/* Collaborator selection halos: colored ring + name pill drawn
                on top of any item another user has selected. Only renders
                anything when at least one collaborator's selection points
                here AND the local user is in a shared room. */}
            <CollaboratorSelections
              collaborators={collaborators}
              furniture={furniture}
              mounts={mounts}
              openings={openings}
              roomPoints={points}
              fit={fit}
              view="design"
              activeWallIndex={null}
              stageScale={panZoom.scale}
            />
          </Layer>

          {/* Drag-preview layer: ghosts of items other collaborators are
              currently mid-drag. The dragging item's Firestore position
              is stale during a drag (see `selfDraggingId` suppression in
              `sharedRoomTypes.ts`), so we read the live position from
              RTDB presence and paint a colored ghost. Without this layer,
              the dragged piece would appear frozen on remote screens
              until the drag ended. */}
          <Layer listening={false}>
            <CollaboratorDragPreviewsDesign
              collaborators={collaborators}
              furniture={furniture}
              fit={fit}
              view="design"
            />
          </Layer>

          {/* Module editor for a modular sectional in "Edit modules" mode. */}
          {interactive && !measureActive && selectedFurniture && editingModules && (
            <Layer>
              <ModuleEditorLayer
                furniture={selectedFurniture}
                modules={editingModules}
                fit={fit}
                stageScale={panZoom.scale}
                selectedModuleId={selectedModuleId}
                onSelectModule={setSelectedModuleId}
                onCommit={(patch) => updateFurniture(selectedFurniture.id, patch)}
              />
            </Layer>
          )}

          {/* Direct-manipulation handles for the selected piece (rotate +
              edge resize). Own layer so they always sit above furniture. */}
          {interactive && !measureActive && selectedFurniture && !editingModules && (
            <Layer>
              <FurnitureHandles
                furniture={selectedFurniture}
                fit={fit}
                stageScale={panZoom.scale}
                unit={unit}
                snapIn={snapEnabled ? gridSnapInches : 0}
                onChange={(patch) => updateFurniture(selectedFurniture.id, patch)}
                onGestureStart={() => beginGesture({ blockUndo: true })}
                onGestureEnd={endGesture}
                onGestureCancel={cancelGesture}
                touch={coarse}
                mode={adjusting ? "adjust" : "direct"}
                sizeAware={adjustModeOn}
                neighbors={handleNeighbors}
                onKnobTap={(field) => onKnobTap?.(selectedFurniture.id, field)}
              />
              <SinkSlideHint
                furniture={selectedFurniture}
                fit={fit}
                stageScale={panZoom.scale}
                hot={sinkHoverId === selectedFurniture.id}
                held={sinkDraggingId === selectedFurniture.id}
              />
            </Layer>
          )}

          {/* The feng shui landing's callouts (issues on the "before" room,
              fixes as the example lands). Its own layer so it sits above
              the furniture and never catches a tap. */}
          <Layer listening={false}>
            <FengShuiDemoLayer pointToPx={fit.pointToPx} stageScale={panZoom.scale} />
          </Layer>

          {/* Cursor layer is on top of everything else and never listens
              for events — collaborator cursors aren't interactive. */}
          {measureActive && (
            <MeasureOverlay
              width={size.width}
              height={size.height}
              fit={fit}
              unit={unit}
              snapEnabled={snapEnabled}
              gridOrigin={gridOrigin}
              gridInches={gridSnapInches}
              stageScale={panZoom.scale}
            />
          )}

          <Layer listening={false}>
            <CollaboratorCursors
              collaborators={collaborators}
              fit={fit}
              roomPoints={points}
              view="design"
              activeWallIndex={null}
              wallLengthIn={null}
              viewport={size}
              stage={{ scale: panZoom.scale, x: panZoom.x, y: panZoom.y }}
            />
          </Layer>

        </Stage>
      )}

      {/* Module tray for the modular sectional being edited. */}
      {editingModules && selectedFurniture && (
        <ModuleTray
          furniture={selectedFurniture}
          modules={editingModules}
          selectedModuleId={selectedModuleId}
          onSelectModule={setSelectedModuleId}
          onCommit={(patch) => updateFurniture(selectedFurniture.id, patch)}
          onDone={() => setEditingModules(null)}
        />
      )}

      {/* Bottom area / ceiling badge — rendered in DOM so it doesn't get
          scaled when the user zooms. */}
      {!hideInfoBadge && (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <div className="pointer-events-auto max-w-[calc(100%-2rem)] truncate rounded-full bg-white px-3 py-1.5 ring-1 ring-ink-200 text-caption font-semibold text-ink-900 shadow-sm">
            {formatArea(area, unit)} · {formatLength(room.ceilingHeight, unit)} {room.finishes?.setting === "outdoor" ? "overhead" : "ceiling"}
          </div>
        </div>
      )}

      {panZoom.isAdjusted && (
        <button
          onClick={panZoom.reset}
          aria-label="Recenter"
          className="absolute right-[calc(0.75rem+var(--safe-right))] bottom-[var(--canvas-overlay-bottom)] z-10 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white ring-1 ring-ink-200 text-xs font-medium text-ink-700 shadow-sm hover:bg-ink-50 active:bg-ink-100 transition-colors"
        >
          <Locate className="size-3.5" />
          Recenter
        </button>
      )}

      {armedWall && enableWallSelect && !measureActive && (() => {
        const m = fit.pointToPx(armedWall.mid);
        const move = armedWall.move;
        const open = armedWall.open;
        return (
          <WallActionChip
            x={m.x * panZoom.scale + panZoom.x}
            y={m.y * panZoom.scale + panZoom.y}
            canvasWidth={size.width}
            onMoveWall={() => {
              setArmedWall(null);
              move();
            }}
            onAddToWall={() => {
              setArmedWall(null);
              open();
            }}
          />
        );
      })()}

      {!demoActive && <GridLegend step={gridStep} unit={unit} />}
      {interactive && (
        <HoldHint
          show={pickup.holdHint || (showAdjustHint && !adjustingId)}
          label={pickup.holdHint ? "Hold a piece to move it" : "Drag to move · Adjust to resize or rotate"}
        />
      )}
    </div>
  );
}

function PersonOverlay({
  positionPx,
  shoulderPx,
  draggable,
  dragDistance,
  onTouchStart,
  onDragStart,
  onDragMove,
  onDragEnd,
}: {
  positionPx: Vec2;
  shoulderPx: number;
  draggable: boolean;
  dragDistance?: number;
  onTouchStart?: (e: KonvaEventObject<TouchEvent>) => void;
  onDragStart?: () => void;
  onDragMove: (p: Vec2) => void;
  onDragEnd?: () => void;
}) {
  const show = useDesign((s) => s.showPerson);
  const demoActive = useFengShuiDemo().phase !== "idle";
  if (!show || demoActive) return null;
  // Body is roughly an oval, with a smaller head circle.
  const bodyW = shoulderPx;
  const bodyH = shoulderPx * 0.65; // depth (shoulder-to-shoulder in front view; from top, body is roughly oval)
  return (
    <Group
      x={positionPx.x}
      y={positionPx.y}
      draggable={draggable}
      dragDistance={dragDistance}
      onTouchStart={onTouchStart}
      onDragStart={() => onDragStart?.()}
      onDragMove={(e) => {
        const node = e.target;
        onDragMove({ x: node.x(), y: node.y() });
      }}
      onDragEnd={() => onDragEnd?.()}
    >
      {/* Personal space halo (~24" radius) */}
      <Circle radius={shoulderPx * 1.6} fill="rgba(42,112,245,0.06)" stroke="rgba(42,112,245,0.35)" strokeWidth={1} dash={[4, 4]} listening={false} />
      {/* Body */}
      <Path
        data={ovalPath(bodyW, bodyH)}
        fill="#2a70f5"
        opacity={0.92}
        shadowColor="#000"
        shadowBlur={10}
        shadowOpacity={0.15}
        shadowOffsetY={3}
      />
      {/* Head */}
      <Circle radius={bodyH * 0.42} fill="#ffffff" />
      <Circle radius={bodyH * 0.42} stroke="#2a70f5" strokeWidth={1.5} />
      {/* Tiny "facing" tick */}
      <Line points={[0, 0, 0, -bodyH * 0.7]} stroke="#2a70f5" strokeWidth={1.5} />
    </Group>
  );
}

function ovalPath(w: number, h: number): string {
  const rx = w / 2;
  const ry = h / 2;
  return `M ${-rx} 0 a ${rx} ${ry} 0 1 0 ${rx * 2} 0 a ${rx} ${ry} 0 1 0 ${-rx * 2} 0 Z`;
}

/**
 * Quarter-circle arc from `from` to `to`, swept around `_center` with the
 * given radius. We pick a sweep direction (0) that produces the natural
 * door-swing appearance for most polygon windings; both `from` and `to` are
 * assumed to be the same radius from the center.
 */
/** Casing projection of a wall cutout, in inches. */
const CUTOUT_JAMB_IN = 6;

function arcPath(
  _center: Vec2,
  from: Vec2,
  to: Vec2,
  radius: number
): string {
  return `M ${from.x} ${from.y} A ${radius} ${radius} 0 0 0 ${to.x} ${to.y}`;
}
